/**
 * Browser side of transcript export: the download click and the clipboard.
 *
 * Split out from `./export.ts` so the formatters stay pure and testable in
 * Node. Everything here touches the DOM and nothing else does.
 */

import type { Transcript, TranscriptExportFormat } from "@shared/types/transcript";
import { EXPORT_MIME, exportTranscript, filenameFor, type ExportOptions } from "./export";

/** Triggers a download without leaking the object URL. */
export function downloadTranscript(transcript: Transcript, options: ExportOptions): void {
  const body = exportTranscript(transcript, options);
  const blob = new Blob([body], { type: EXPORT_MIME[options.format] });
  const url = URL.createObjectURL(blob);

  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filenameFor(transcript, options.format);
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();

  // Revoking immediately cancels the download in Safari; one tick is enough.
  setTimeout(() => URL.revokeObjectURL(url), 1_000);
}

export async function copyToClipboard(text: string): Promise<boolean> {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      return legacyCopy(text);
    }
  }
  return legacyCopy(text);
}

/**
 * `document.execCommand("copy")` is deprecated but remains the only path that
 * works when the async clipboard API is unavailable or blocked.
 */
function legacyCopy(text: string): boolean {
  try {
    const area = document.createElement("textarea");
    area.value = text;
    area.setAttribute("readonly", "");
    area.style.position = "fixed";
    area.style.opacity = "0";
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand("copy");
    area.remove();
    return ok;
  } catch {
    return false;
  }
}

export type { ExportOptions, TranscriptExportFormat };
